// Web report/PDF orchestration — mirrors mobile app/reports/edit.tsx.
// Uses the ported byte-identical PDF engine (src/pdf/pdfService.ts):
// build HTML -> POST /api/reports/render-html-to-pdf (WeasyPrint) ->
// blob URL (opened for preview by htmlToPdf) -> we also trigger a
// file download and best-effort persist into Saved Reports.
import { pdfService } from "@/pdf/pdfService";
import { toast } from "sonner";
import {
  quotesApi,
  companiesApi,
  contractorsApi,
  buildingsApi,
  doorsApi,
  inspectionsApi,
  maintenanceApi,
} from "@/services/api";

const dateTag = () => new Date().toISOString().slice(0, 10);
const clean = (s, fallback) =>
  String(s || "").replace(/[/\\?%*:|"<>]/g, "").trim() || fallback;

const apiMsg = (e, fallback) =>
  e?.response?.data?.detail || e?.message || fallback;

// Persist a freshly-rendered report to the Saved Reports library and tell
// the user the outcome. The backend render job usually saves server-side
// already (pdfService caches the returned saved_report_id), so this mostly
// just confirms success; otherwise it POSTs and surfaces any failure as a
// toast instead of swallowing it silently.
async function persistReport(url, meta) {
  try {
    const saved = await pdfService.saveToBackend(url, meta);
    if (saved?.id) toast.success("Saved to Reports.");
    else if (globalThis.__fdcheck_render_backgrounded) {
      // Large report still finishing server-side; it self-saves to the
      // library, so don't surface a failure — just point the user there.
      toast("Your report is saving to Reports in the background — check the Reports list in a moment.", { duration: 10000 });
    }
  } catch (e) {
    toast.error(
      `Report generated but couldn't be saved to Reports: ${apiMsg(e, "please try again")}`,
      { duration: 9000 },
    );
  }
}

function triggerDownload(url, filename) {
  if (!url) return false;
  // htmlToPdf() caches the REAL PDF bytes on window.__fdcheck_pdf_cache[url]
  // only when the server-side WeasyPrint render succeeded. If that entry is
  // missing, the render failed and htmlToPdf fell back to opening the raw
  // report HTML in a new tab. Downloading THAT blob with a ".pdf" name
  // produces an unopenable file ("We can't open this file / 0 pages"), so
  // skip the download and point the user at the printable preview instead.
  const isRealPdf = !!(globalThis.__fdcheck_pdf_cache && globalThis.__fdcheck_pdf_cache[url]);
  if (!isRealPdf) {
    if (globalThis.__fdcheck_view_only_preview) {
      try { delete globalThis.__fdcheck_view_only_preview; } catch { /* ignore */ }
      // Durable marker so the Report Builder page knows NOT to fire its generic
      // "saved to Reports" success toast (nothing was downloaded or persisted).
      try { globalThis.__fdcheck_last_view_only = true; } catch { /* ignore */ }
      toast("Reports are view-only on the free trial. We opened a read-only preview — subscribe to download, print or share a PDF.", { duration: 11000 });
    } else if (globalThis.__fdcheck_render_backgrounded) {
      toast("This is a large report and is still finishing in the background. It will appear in your Reports list in a moment — open it from there to view and download.", { duration: 12000 });
    } else {
      toast.error(
        "The PDF service was unavailable, so we opened a printable version in a new tab. Use your browser's Print \u2192 Save as PDF to download it.",
        { duration: 10000 },
      );
    }
    return false;
  }
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "fdcheck-report.pdf";
    document.body.appendChild(a);
    a.click();
    a.remove();
  } catch (e) {
    console.warn("[pdf] download trigger failed (preview tab still works)", e);
  }
  return true;
}

export async function downloadQuotePdf(quoteId, opts = {}) {
  const {
    coverLetter = null,
    terms = null,
    customSections = null,
    includeCompliance = true,
    displayMode: displayModeOverride = null,
    showDoorPhotos: showDoorPhotosOverride = null,
    photoAppendix = false,
    download = true,
  } = opts;
  const [{ data: quote }, mc] = await Promise.all([
    quotesApi.getOne(quoteId),
    companiesApi.me().then((r) => r.data).catch(() => ({})),
  ]);
  const showDoorPhotos = showDoorPhotosOverride != null
    ? showDoorPhotosOverride
    : mc?.quote_show_door_photos !== false;
  let doorPhotos = [];
  if (showDoorPhotos || photoAppendix) {
    try {
      const dp = await quotesApi.doorPhotos(quoteId);
      doorPhotos = Array.isArray(dp.data?.photos) ? dp.data.photos : [];
    } catch (e) {
      console.warn("[pdf] door photos fetch failed (non-fatal)", e);
    }
  }
  // Per-generation cover letter / terms overrides (from the "Customise
  // PDF" dialog) ride in on a shallow copy of the company blob, which is
  // where the quote builder reads intro/terms from.
  const company = { ...(mc || {}) };
  if (coverLetter != null) company.quote_intro_wording = coverLetter;
  if (terms != null) company.quote_terms = terms;
  const displayMode = displayModeOverride || quote.display_mode || mc?.quote_display_mode || null;
  const qNum = String(quoteId).slice(-8);
  const fileName = `fdcheck-quote-${qNum}-${dateTag()}.pdf`;
  const saveMeta = download ? {
    building_id: quote.building_id || null,
    door_id: quote.door_id || null,
    quote_id: quoteId,
    report_type: "quote",
    title: quote.title || `Quote ${qNum}`,
    file_name: fileName,
    summary: { total: quote.total, status: quote.status },
  } : null;
  if (saveMeta) { try { globalThis.__fdcheck_pending_save_meta = saveMeta; } catch { /* ignore */ } }
  let url;
  try {
    url = await pdfService.generateQuotePDF(quote, {
      maintenanceCompany: company,
      customSections: customSections || undefined,
      includeCompliance,
      displayMode,
      doorPhotos,
      showDoorPhotos,
      photoAppendix,
    });
  } finally {
    try { delete globalThis.__fdcheck_pending_save_meta; } catch { /* ignore */ }
  }
  if (download) {
    triggerDownload(url, fileName);
    await persistReport(url, saveMeta);
  }
  return url;
}

export async function downloadInvoicePdf(invoice) {
  const [companyR, contractorR] = await Promise.all([
    companiesApi.me().catch(() => ({ data: {} })),
    invoice.issuer_contractor_id
      ? contractorsApi.get(invoice.issuer_contractor_id).catch(() => ({ data: {} }))
      : Promise.resolve({ data: {} }),
  ]);
  const url = await pdfService.generateInvoicePDF(invoice, {
    maintenanceCompany: companyR.data || {},
    contractor: contractorR.data || {},
  });
  const num = clean(invoice.invoice_number || invoice.id || "draft", "draft");
  triggerDownload(url, `fdcheck-invoice-${num}.pdf`);
  return url;
}

export async function generateDoorReport(doorId, buildingId, opts = {}) {
  const {
    customSections = null,
    includeCompliance = true,
    download = true,
    persist = true,
  } = opts;
  const [doorR, buildingR, inspR] = await Promise.all([
    doorsApi.getOne(doorId),
    buildingsApi.getOne(buildingId),
    inspectionsApi.getAll({ door_id: doorId }),
  ]);
  const inspection = (inspR.data || []).length > 0 ? inspR.data[0] : null;
  const buildingLabel = clean(buildingR.data?.name, "Building");
  const doorLabel = doorR.data?.apartment_number
    ? `Apt ${clean(doorR.data.apartment_number, "")}`
    : clean(doorR.data?.door_reference, "Door");
  const suffix = inspection ? "Inspection" : "Survey";
  const fileName = `${buildingLabel} - ${doorLabel} - ${suffix} ${dateTag()}.pdf`;
  const saveMeta = persist ? {
    building_id: buildingId,
    door_id: doorId,
    inspection_id: inspection?.id || null,
    report_type: "door_inspection",
    title: `${doorR.data?.door_reference || "Door"} — ${inspection ? "Inspection report" : "Survey report"}`,
    file_name: fileName,
    summary: {
      status: inspection?.status || "uninspected",
      inspection_date: inspection?.inspection_date || null,
    },
  } : null;
  if (saveMeta) { try { globalThis.__fdcheck_pending_save_meta = saveMeta; } catch { /* ignore */ } }
  let url;
  try {
    url = await pdfService.generateDoorReport({
      building: buildingR.data,
      door: doorR.data,
      inspection,
      customSections: customSections || undefined,
      includeCompliance,
    });
  } finally {
    try { delete globalThis.__fdcheck_pending_save_meta; } catch { /* ignore */ }
  }
  if (download) triggerDownload(url, fileName);
  if (persist) await persistReport(url, saveMeta);
  return url;
}

export async function generateBuildingReport(buildingId, opts = {}) {
  const {
    customSections = null,
    includeCompliance = true,
    selectedDoorIds = null,
    download = true,
    persist = true,
  } = opts;
  const [buildingR, doorsR, inspR, jobsR, trackR] = await Promise.all([
    buildingsApi.getOne(buildingId),
    doorsApi.getAll(buildingId),
    inspectionsApi.getAll({ building_id: buildingId, _fresh: true }),
    maintenanceApi.list().catch(() => ({ data: [] })),
    buildingsApi.remedialTracking(buildingId).catch(() => ({ data: { items: [] } })),
  ]);
  const allJobs = Array.isArray(jobsR.data) ? jobsR.data : jobsR.data?.items || [];
  const maintenanceJobs = allJobs.filter((j) => j?.building_id === buildingId);
  const allDoors = doorsR.data || [];
  const allInsp = inspR.data || [];

  // Honour the author's door selection (null = all doors).
  let doors = allDoors;
  let inspections = allInsp;
  let partial = false;
  if (Array.isArray(selectedDoorIds)) {
    const keep = new Set(selectedDoorIds.map(String));
    doors = allDoors.filter((d) => keep.has(String(d.id)));
    inspections = allInsp.filter((i) => keep.has(String(i.door_id)));
    if (doors.length === 0) throw new Error("No doors selected — pick at least one door for the report.");
    partial = doors.length < allDoors.length;
  }

  const bldName = clean(buildingR.data?.name, "Building");
  const hasInsp = inspections.length > 0;
  const stats = inspections.reduce(
    (acc, i) => {
      const s = String(i.status || "").toLowerCase();
      if (s === "pass") acc.pass++;
      else if (s === "fail") acc.fail++;
      else if (s === "review") acc.review++;
      return acc;
    },
    { pass: 0, fail: 0, review: 0 }
  );
  const fileName = `${bldName} - ${hasInsp ? "Fire Door Inspection Report" : "Fire Door Register"}${partial ? ` (${doors.length} doors)` : ""} ${dateTag()}.pdf`;
  const saveMeta = persist ? {
    building_id: buildingId,
    report_type: "building_summary",
    title: partial
      ? `${buildingR.data?.name || "Building"} — Selected doors (${doors.length} of ${allDoors.length})`
      : `${buildingR.data?.name || "Building"} — Full report`,
    file_name: fileName,
    summary: { doors: doors.length, doors_on_register: allDoors.length, inspections: inspections.length, ...stats },
  } : null;
  if (saveMeta) { try { globalThis.__fdcheck_pending_save_meta = saveMeta; } catch { /* ignore */ } }
  let url;
  try {
    url = await pdfService.generateBuildingReport({
      building: buildingR.data,
      doors,
      inspections,
      maintenanceJobs,
      customSections: customSections || undefined,
      includeCompliance,
      remedialTracking: trackR.data?.items || [],
      doorSelection: partial ? { selected: doors.length, total: allDoors.length } : undefined,
    });
  } finally {
    try { delete globalThis.__fdcheck_pending_save_meta; } catch { /* ignore */ }
  }
  if (download) triggerDownload(url, fileName);
  if (persist) await persistReport(url, saveMeta);
  return url;
}
